import { DateTime } from 'luxon';
import type { AnalyticsControllerGetOrdersDrilldownOrdersData } from '@/generated/korza-api-client/types.gen';
import type { Locales } from '@/i18n/i18n-types';
import type { I18nToken } from '@/i18n/type-utils';
import { formatChartAxisLabel, type ChartGranularity } from '@/lib/chart-axis';

export const REPURCHASE_SEGMENTS = [
  'first_purchase',
  'in_app',
  'channel_migration',
  'total_repurchase',
] as const;

export type RepurchaseSegment = (typeof REPURCHASE_SEGMENTS)[number];

/** Data source understood by the backend (`source` query param). */
export type OrdersDrilldownSource =
  AnalyticsControllerGetOrdersDrilldownOrdersData['query']['source'];

/** What the user clicked; decides the source, the title and the controls. */
export const DRILLDOWN_ORIGINS = [
  ...REPURCHASE_SEGMENTS,
  'revenue',
  'paid_orders',
  'average_ticket',
  'campaign_orders_count',
  'campaign_orders_revenue',
  'orders_completed',
  'orders_not_completed',
] as const;

export type DrilldownOrigin = (typeof DRILLDOWN_ORIGINS)[number];

/** What the amount of the list means: sold revenue or not yet paid value. */
export type DrilldownAmountKind = 'revenue' | 'unrealized';

export interface DrilldownOriginConfig {
  source: OrdersDrilldownSource;
  titleToken: I18nToken;
  /** First purchases cannot be narrowed to one chart bucket. */
  supportsBucket: boolean;
  /** Shows the repurchase segment filter and the order type column. */
  isRepurchase: boolean;
  /** Decides the summary strip: revenue and average ticket, or unrealized amount. */
  amountKind: DrilldownAmountKind;
}

export const DRILLDOWN_ORIGIN_CONFIG: Record<
  DrilldownOrigin,
  DrilldownOriginConfig
> = {
  first_purchase: {
    source: 'first_purchase',
    titleToken: 'metricsPage.ordersComposition.firstPurchase.label',
    supportsBucket: false,
    isRepurchase: true,
    amountKind: 'revenue',
  },
  in_app: {
    source: 'in_app',
    titleToken: 'metricsPage.ordersComposition.inAppRepurchase.label',
    supportsBucket: true,
    isRepurchase: true,
    amountKind: 'revenue',
  },
  channel_migration: {
    source: 'channel_migration',
    titleToken: 'metricsPage.ordersComposition.channelMigration.label',
    supportsBucket: true,
    isRepurchase: true,
    amountKind: 'revenue',
  },
  total_repurchase: {
    source: 'total_repurchase',
    titleToken: 'metricsPage.repurchases.title',
    supportsBucket: true,
    isRepurchase: true,
    amountKind: 'revenue',
  },
  revenue: {
    source: 'paid_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.revenue' satisfies I18nToken,
    supportsBucket: true,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  paid_orders: {
    source: 'paid_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.paid_orders' satisfies I18nToken,
    supportsBucket: true,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  average_ticket: {
    source: 'paid_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.average_ticket' satisfies I18nToken,
    supportsBucket: true,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  campaign_orders_count: {
    source: 'campaign_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.campaign_orders_count' satisfies I18nToken,
    supportsBucket: true,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  campaign_orders_revenue: {
    source: 'campaign_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.campaign_orders_revenue' satisfies I18nToken,
    supportsBucket: true,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  orders_completed: {
    source: 'paid_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.orders_completed' satisfies I18nToken,
    supportsBucket: false,
    isRepurchase: false,
    amountKind: 'revenue',
  },
  orders_not_completed: {
    source: 'unpaid_orders',
    titleToken:
      'metricsPage.ordersDrilldown.titles.orders_not_completed' satisfies I18nToken,
    supportsBucket: false,
    isRepurchase: false,
    amountKind: 'unrealized',
  },
};

export type OrdersCompletionSegment = 'completed' | 'notCompleted';

/** The pie slice the user clicked decides which list opens. */
export const ORDERS_COMPLETION_SEGMENT_ORIGIN: Record<
  OrdersCompletionSegment,
  DrilldownOrigin
> = {
  completed: 'orders_completed',
  notCompleted: 'orders_not_completed',
};

/** An empty pie, or one showing mocked data, has no list to open. */
export function isOrdersCompletionClickable(input: {
  completedCount: number;
  notCompletedCount: number;
  enabled: boolean;
}): boolean {
  return input.enabled && input.completedCount + input.notCompletedCount > 0;
}

export type DrilldownStatKind =
  | 'orders'
  | 'customers'
  | 'revenue'
  | 'averageTicket'
  | 'unrealizedAmount';

/** Summary strip of the dialog, in display order. */
export function getDrilldownStatKinds(
  amountKind: DrilldownAmountKind,
  view: DrilldownView
): readonly DrilldownStatKind[] {
  if (amountKind === 'unrealized') {
    return view === 'orders'
      ? ['orders', 'unrealizedAmount']
      : ['customers', 'orders', 'unrealizedAmount'];
  }
  return view === 'orders'
    ? ['orders', 'revenue', 'averageTicket']
    : ['customers', 'orders', 'revenue'];
}

/** Segments the user can switch between inside the sheet (bucket is kept). */
export const REPURCHASE_FILTER_SEGMENTS = [
  'total_repurchase',
  'in_app',
  'channel_migration',
] as const;
export type RepurchaseFilterSegment =
  (typeof REPURCHASE_FILTER_SEGMENTS)[number];

export function isRepurchaseFilterSegment(
  segment: DrilldownOrigin | OrdersDrilldownSource
): segment is RepurchaseFilterSegment {
  return REPURCHASE_FILTER_SEGMENTS.some((s) => s === segment);
}

/** Sized so a page fits the full-screen dialog without scrolling on desktop. */
export const DRILLDOWN_PAGE_SIZE = 10;
/** Page of the mobile sheet's "Carregar mais" list. */
export const DRILLDOWN_MOBILE_PAGE_SIZE = 20;

export const DRILLDOWN_VIEWS = ['orders', 'customers'] as const;
export type DrilldownView = (typeof DRILLDOWN_VIEWS)[number];

export const ORDER_SORTS = [
  'date_desc',
  'date_asc',
  'amount_desc',
  'amount_asc',
] as const;
export type OrderSort = (typeof ORDER_SORTS)[number];

export const CUSTOMER_SORTS = [
  'total_spent_desc',
  'total_spent_asc',
  'order_count_desc',
  'order_count_asc',
] as const;
export type CustomerSort = (typeof CUSTOMER_SORTS)[number];

export const DEFAULT_ORDER_SORT: OrderSort = 'date_desc';
export const DEFAULT_CUSTOMER_SORT: CustomerSort = 'total_spent_desc';

/** Matches the backend limit for the `search` param. */
export const DRILLDOWN_SEARCH_MAX_LENGTH = 100;
/** Delay between the last keystroke and the server-side search. */
export const DRILLDOWN_SEARCH_DEBOUNCE_MS = 300;

export interface DrilldownState {
  origin: DrilldownOrigin | null;
  bucket: string | null;
  page: number;
  view: DrilldownView;
  /** Raw (unvalidated) search text from the URL. */
  search: string | null;
  /** Raw (unvalidated) sort from the URL; invalid for the view means default. */
  sort: string | null;
}

export interface DrilldownFilter {
  fromDate: string;
  toDate: string;
  granularity: ChartGranularity;
}

interface DrilldownQueryBase {
  fromDate: string;
  toDate: string;
  source: OrdersDrilldownSource;
  bucketStart?: string;
  granularity?: ChartGranularity;
  search?: string;
  page: number;
  limit: number;
}

/** `sort` is only present when it differs from the backend default. */
export type DrilldownQuery = DrilldownQueryBase &
  (
    | { view: 'orders'; sort?: OrderSort }
    | { view: 'customers'; sort?: CustomerSort }
  );

export function isRepurchaseSegment(
  value: unknown
): value is RepurchaseSegment {
  return REPURCHASE_SEGMENTS.some((segment) => segment === value);
}

export function isDrilldownOrigin(value: unknown): value is DrilldownOrigin {
  return DRILLDOWN_ORIGINS.some((origin) => origin === value);
}

export function isDrilldownView(value: unknown): value is DrilldownView {
  return DRILLDOWN_VIEWS.some((view) => view === value);
}

export function isOrderSort(value: unknown): value is OrderSort {
  return ORDER_SORTS.some((sort) => sort === value);
}

export function isCustomerSort(value: unknown): value is CustomerSort {
  return CUSTOMER_SORTS.some((sort) => sort === value);
}

export function getSortsForView(
  view: DrilldownView
): readonly (OrderSort | CustomerSort)[] {
  return view === 'orders' ? ORDER_SORTS : CUSTOMER_SORTS;
}

export function getDefaultSort(view: DrilldownView): OrderSort | CustomerSort {
  return view === 'orders' ? DEFAULT_ORDER_SORT : DEFAULT_CUSTOMER_SORT;
}

/** A sort that does not belong to the view (or is unknown) falls back to the default. */
export function resolveDrilldownSort(
  view: DrilldownView,
  sort: string | null
): OrderSort | CustomerSort {
  if (view === 'orders') return isOrderSort(sort) ? sort : DEFAULT_ORDER_SORT;
  return isCustomerSort(sort) ? sort : DEFAULT_CUSTOMER_SORT;
}

/** Trims and caps the search text; blank means no search (null). */
export function normalizeDrilldownSearch(
  search: string | null | undefined
): string | null {
  const trimmed = (search ?? '').trim().slice(0, DRILLDOWN_SEARCH_MAX_LENGTH);
  return trimmed.length > 0 ? trimmed : null;
}

/** Switching view goes back to page 1 and keeps segment, bucket and search. */
export function changeDrilldownView(
  state: DrilldownState,
  view: DrilldownView
): DrilldownState {
  if (state.view === view) return state;
  return { ...state, view, page: 1, sort: null };
}

/** Switching segment goes back to page 1 and keeps bucket, view and search. */
export function changeDrilldownSegment(
  state: DrilldownState,
  segment: RepurchaseSegment
): DrilldownState {
  if (state.origin === segment) return state;
  return { ...state, origin: segment, page: 1 };
}

export function changeDrilldownSearch(
  state: DrilldownState,
  search: string | null
): DrilldownState {
  return { ...state, search: normalizeDrilldownSearch(search), page: 1 };
}

export function changeDrilldownSort(
  state: DrilldownState,
  sort: OrderSort | CustomerSort
): DrilldownState {
  return { ...state, sort, page: 1 };
}

/** Returns the bucket ISO unchanged when valid, otherwise null. */
export function parseDrilldownBucket(bucket: string | null): string | null {
  if (!bucket) return null;
  return DateTime.fromISO(bucket).isValid ? bucket : null;
}

/** Null means the drill-down is closed (no origin selected). */
export function buildDrilldownQuery(
  state: DrilldownState,
  filter: DrilldownFilter
): DrilldownQuery | null {
  if (state.origin === null) return null;

  const config = DRILLDOWN_ORIGIN_CONFIG[state.origin];
  const search = normalizeDrilldownSearch(state.search);
  const base: DrilldownQueryBase = {
    fromDate: filter.fromDate,
    toDate: filter.toDate,
    source: config.source,
    page: Math.max(1, Math.floor(state.page)),
    limit: DRILLDOWN_PAGE_SIZE,
  };
  if (search !== null) base.search = search;

  const bucket = parseDrilldownBucket(state.bucket);
  if (bucket !== null && config.supportsBucket) {
    base.bucketStart = bucket;
    base.granularity = filter.granularity;
  }

  if (state.view === 'customers') {
    const sort = resolveDrilldownSort('customers', state.sort);
    return isCustomerSort(sort) && sort !== DEFAULT_CUSTOMER_SORT
      ? { ...base, view: 'customers', sort }
      : { ...base, view: 'customers' };
  }
  const sort = resolveDrilldownSort('orders', state.sort);
  return isOrderSort(sort) && sort !== DEFAULT_ORDER_SORT
    ? { ...base, view: 'orders', sort }
    : { ...base, view: 'orders' };
}

/**
 * Whether two drill-down queries cover the same period and bucket. Only then
 * is it safe to keep showing the previous response while the next one loads
 * (page, sort, search or segment changes); a different period or bucket must
 * never show the numbers of the previous one.
 */
export function isSameDrilldownPeriod(
  previous: DrilldownQuery | null | undefined,
  next: DrilldownQuery | null | undefined
): boolean {
  if (!previous || !next) return false;
  return (
    previous.fromDate === next.fromDate &&
    previous.toDate === next.toDate &&
    previous.bucketStart === next.bucketStart &&
    previous.granularity === next.granularity
  );
}

export function getDrilldownBucketLabel(
  bucket: string | null,
  granularity: ChartGranularity,
  locale: Locales
): string | null {
  const parsed = parseDrilldownBucket(bucket);
  if (parsed === null) return null;
  return formatChartAxisLabel(DateTime.fromISO(parsed), granularity, locale);
}

function readStatusCode(error: unknown): number | null {
  if (typeof error !== 'object' || error === null) return null;
  const statusCode: unknown =
    'statusCode' in error ? error.statusCode : undefined;
  return typeof statusCode === 'number' ? statusCode : null;
}

/** Maps a CSV download failure to the i18n token shown to the user. */
export function getCsvErrorToken(error: unknown): I18nToken {
  return readStatusCode(error) === 422
    ? ('metricsPage.repurchaseDrilldown.csvTooLarge' satisfies I18nToken)
    : ('metricsPage.repurchaseDrilldown.csvError' satisfies I18nToken);
}

/** Resolves a dotted i18n token (e.g. `a.b.title`) against `LL` and calls it. */
export function translateToken(LL: object, token: string): string {
  let node: unknown = LL;
  for (const key of token.split('.')) {
    if (
      (typeof node !== 'object' && typeof node !== 'function') ||
      node === null
    ) {
      return '';
    }
    node = Reflect.get(node, key);
  }
  if (typeof node !== 'function') return '';
  const text: unknown = node();
  return typeof text === 'string' ? text : '';
}

const CSV_SLUGS: Record<DrilldownOrigin, { en: string; pt: string }> = {
  first_purchase: { en: 'repurchases', pt: 'recompras' },
  in_app: { en: 'repurchases', pt: 'recompras' },
  channel_migration: { en: 'repurchases', pt: 'recompras' },
  total_repurchase: { en: 'repurchases', pt: 'recompras' },
  revenue: { en: 'revenue', pt: 'receita' },
  paid_orders: { en: 'paid-orders', pt: 'pedidos-pagos' },
  average_ticket: { en: 'average-ticket', pt: 'ticket-medio' },
  campaign_orders_count: { en: 'campaign-orders', pt: 'pedidos-campanha' },
  campaign_orders_revenue: { en: 'campaign-revenue', pt: 'receita-campanha' },
  orders_completed: { en: 'completed-orders', pt: 'pedidos-concluidos' },
  orders_not_completed: {
    en: 'not-completed-orders',
    pt: 'pedidos-nao-concluidos',
  },
};

export function getDrilldownCsvFileName(
  origin: DrilldownOrigin,
  locale: Locales,
  tenantSlug: string,
  timestamp: string | null
): string {
  const slug = CSV_SLUGS[origin][locale === 'pt' ? 'pt' : 'en'];
  return `korza-${slug}-${tenantSlug}-${timestamp}.csv`;
}
